import ArticleIcon from "@mui/icons-material/Article";
import DirectoryIcon from "@mui/icons-material/FolderSpecial";
import GroupIcon from "@mui/icons-material/Group";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import SearchIcon from "@mui/icons-material/Search";
import { Bot } from "lucide-react";
import { SvgIcon } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TwitterIcon from "@mui/icons-material/Twitter";
import YouTubeIcon from "@mui/icons-material/YouTube";
import {
  Box,
  Button,
  Card,
  CardContent,
  Fade,
  FormControl,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useState } from "react";

// Wrapper to make Lucide Bot icon compatible with MUI sx prop
const BotIcon = (props: any) => (
  <SvgIcon {...props}>
    <Bot />
  </SvgIcon>
);

const ReferralContainer = styled(Box)(({ theme }) => ({
  width: "100%",
}));

const OptionCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected: boolean }>(({ theme, selected }) => ({
    cursor: "pointer",
    transition: "all 0.3s ease",
    border: selected
      ? `2px solid ${theme.palette.primary.main}`
      : "2px solid transparent",
    backgroundColor: selected
      ? theme.palette.primary.main + "08"
      : "transparent",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
      borderColor: selected
        ? theme.palette.primary.main
        : theme.palette.grey[300],
    },
  })
);

const referralOptions = [
  { value: "llm", label: "LLM (ChatGPT)", icon: BotIcon },
  { value: "google_search", label: "Google Search", icon: SearchIcon },
  { value: "colleague", label: "Colleague", icon: GroupIcon },
  { value: "linkedin", label: "LinkedIn", icon: LinkedInIcon },
  { value: "twitter", label: "Twitter/X", icon: TwitterIcon },
  { value: "youtube", label: "YouTube", icon: YouTubeIcon },
  { value: "blog_article", label: "Blog or Article", icon: ArticleIcon },
  { value: "online_ad", label: "Online Ad", icon: TrendingUpIcon },
  { value: "ai_directory", label: "AI Directory", icon: DirectoryIcon },
  { value: "other", label: "Other", icon: MoreHorizIcon },
];

interface ReferralStepProps {
  onSubmit: (referralSource: string) => void;
  onBack: () => void;
  appName: string;
}

export default function ReferralStep({
  onSubmit,
  onBack,
  appName,
}: ReferralStepProps) {
  const [selectedSource, setSelectedSource] = useState("");
  const theme = useTheme();

  const handleSubmit = () => {
    if (selectedSource) {
      onSubmit(selectedSource);
    }
  };

  return (
    <ReferralContainer>
      <Fade in={true} timeout={800}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              mb: 2,
              color: "text.primary",
            }}
          >
            How did you hear about {appName}?
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 4, lineHeight: 1.6 }}
          >
            Help us understand how you discovered us so we can continue to reach
            people like you!
          </Typography>

          <FormControl component="fieldset" sx={{ width: "100%" }}>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {referralOptions.map((option) => {
                const IconComponent = option.icon;
                const isSelected = selectedSource === option.value;

                return (
                  <Grid item xs={12} sm={6} md={6} lg={4} key={option.value}>
                    <OptionCard
                      selected={isSelected}
                      onClick={() => setSelectedSource(option.value)}
                    >
                      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            textAlign: "center",
                          }}
                        >
                          <IconComponent
                            sx={{
                              fontSize: 32,
                              mb: 1,
                              color: isSelected
                                ? "primary.main"
                                : "text.secondary",
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: isSelected ? 600 : 400,
                              color: isSelected
                                ? "primary.main"
                                : "text.primary",
                            }}
                          >
                            {option.label}
                          </Typography>
                        </Box>
                      </CardContent>
                    </OptionCard>
                  </Grid>
                );
              })}
            </Grid>
          </FormControl>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button variant="outlined" onClick={onBack} sx={{ px: 3 }}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!selectedSource}
              sx={{
                px: 4,
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              Continue
            </Button>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 2, display: "block" }}
          >
            This helps us improve our outreach and find more amazing users like
            you
          </Typography>
        </Box>
      </Fade>
    </ReferralContainer>
  );
}
